// Modal that lets the user remap keyboard keys and gamepad buttons,
// with one tab per player (couch co-op assigns a different input device
// to each emulator tile).
//
// Built as a native <dialog>. The harness creates one instance at boot
// and calls open() from the Controls button. While the modal is open the
// InputHub is paused so the user can press arrow keys or face buttons
// without those driving the emulators.

import {
  NES_BUTTON_NAMES,
  addKeyboardBinding,
  addGamepadBinding,
  removeKeyboardBinding,
  removeGamepadBinding,
  resetPlayerBindings,
  getKeyboardMap,
  getGamepadMap,
  getPadIndex,
  setPadIndex,
  formatKeyCode,
  formatGamepadButton,
  onChange,
} from "./bindings.js";

// What the modal needs from the rest of the player: pause/resume the
// shared input hub, and how many players (tiles) are currently active.
export interface ControlsModalDeps {
  setInputEnabled: (enabled: boolean) => void;
  getPlayerCount: () => number;
}

// Snapshot of which buttons on a gamepad are currently pressed, keyed by
// button index. Used to detect "newly pressed" edges during capture.
type PadState = Set<number>;

function snapshotPad(pad: Gamepad | null): PadState {
  const s: PadState = new Set();
  if (!pad) return s;
  for (let i = 0; i < pad.buttons.length; i++) {
    if (pad.buttons[i]?.pressed) s.add(i);
  }
  return s;
}

function padAtIndex(index: number | null): Gamepad | null {
  if (index === null) return null;
  const pads = navigator.getGamepads ? navigator.getGamepads() : [];
  return pads[index] ?? null;
}

function connectedPads(): Gamepad[] {
  const pads = navigator.getGamepads ? navigator.getGamepads() : [];
  const out: Gamepad[] = [];
  for (const p of pads) {
    if (p && p.connected) out.push(p);
  }
  return out;
}

export class ControlsModal {
  private dialog: HTMLDialogElement;
  private body: HTMLElement;
  private deps: ControlsModalDeps;
  private unsubscribe: (() => void) | null = null;
  // Active "press a key" capture (per row). Stored so we can cancel if
  // the user clicks another row or closes the modal.
  private cancelCapture: (() => void) | null = null;
  // Which player tab is selected. Clamped to the active player count
  // each render (closing a tile can drop the tab you were on).
  private activePlayer = 0;

  constructor(dialog: HTMLDialogElement, body: HTMLElement, deps: ControlsModalDeps) {
    this.dialog = dialog;
    this.body = body;
    this.deps = deps;

    this.dialog.addEventListener("close", () => {
      this.cancelActiveCapture();
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      window.removeEventListener("gamepadconnected", this.onPadHotplug);
      window.removeEventListener("gamepaddisconnected", this.onPadHotplug);
      this.deps.setInputEnabled(true);
    });

    // Click on the backdrop (anywhere outside the dialog content) to close.
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
  }

  // Optionally open focused on a specific player's tab (a tile's
  // "Controls" tab passes its own slot). render() clamps it to the
  // active player count.
  open(player?: number): void {
    if (this.dialog.open) return;
    if (player !== undefined) this.activePlayer = player;
    this.deps.setInputEnabled(false);
    this.unsubscribe = onChange(() => this.render());
    // Re-render on pad hotplug so the gamepad assignment dropdown stays
    // in sync while the modal is open.
    window.addEventListener("gamepadconnected", this.onPadHotplug);
    window.addEventListener("gamepaddisconnected", this.onPadHotplug);
    this.render();
    this.dialog.showModal();
  }

  private onPadHotplug = (): void => { this.render(); };

  private cancelActiveCapture(): void {
    if (this.cancelCapture) {
      this.cancelCapture();
      this.cancelCapture = null;
    }
  }

  private render(): void {
    this.cancelActiveCapture();
    this.body.innerHTML = "";

    const playerCount = Math.max(1, this.deps.getPlayerCount());
    if (this.activePlayer >= playerCount) this.activePlayer = playerCount - 1;

    this.body.append(this.renderTabs(playerCount));
    this.body.append(this.renderPadAssignment(this.activePlayer));
    this.body.append(this.renderBindingsTable(this.activePlayer));
    this.body.append(this.renderFooter(this.activePlayer));
  }

  private renderTabs(playerCount: number): HTMLElement {
    const tabs = document.createElement("div");
    tabs.className = "player-tabs";
    // Single-player setups don't need a tab strip.
    if (playerCount <= 1) return tabs;
    for (let p = 0; p < playerCount; p++) {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.className = "player-tab" + (p === this.activePlayer ? " active" : "");
      tab.textContent = `Player ${p + 1}`;
      tab.addEventListener("click", () => {
        this.activePlayer = p;
        this.render();
      });
      tabs.append(tab);
    }
    return tabs;
  }

  private renderPadAssignment(player: number): HTMLElement {
    const row = document.createElement("div");
    row.className = "pad-assign";

    const label = document.createElement("span");
    label.className = "pad-assign-label";
    label.textContent = "Gamepad";
    row.append(label);

    const select = document.createElement("select");
    select.className = "pad-select";

    const none = document.createElement("option");
    none.value = "";
    none.textContent = "None (keyboard only)";
    select.append(none);

    const current = getPadIndex(player);
    for (const pad of connectedPads()) {
      const opt = document.createElement("option");
      opt.value = String(pad.index);
      const shortId = pad.id.length > 32 ? pad.id.slice(0, 32) + "…" : pad.id;
      opt.textContent = `#${pad.index} — ${shortId}`;
      if (pad.index === current) opt.selected = true;
      select.append(opt);
    }
    // A previously-assigned pad that's currently disconnected still shows
    // so the assignment is visible (and not silently dropped).
    if (current !== null && !connectedPads().some(p => p.index === current)) {
      const opt = document.createElement("option");
      opt.value = String(current);
      opt.textContent = `#${current} — (disconnected)`;
      opt.selected = true;
      select.append(opt);
    }

    select.addEventListener("change", () => {
      const v = select.value;
      setPadIndex(player, v === "" ? null : Number(v));
    });
    row.append(select);
    return row;
  }

  private renderBindingsTable(player: number): HTMLElement {
    const table = document.createElement("table");
    table.className = "bindings-table";

    const head = document.createElement("thead");
    head.innerHTML = `<tr>
      <th>NES Button</th>
      <th>Keyboard</th>
      <th>Gamepad</th>
    </tr>`;
    table.append(head);

    const tbody = document.createElement("tbody");
    const keyboard = getKeyboardMap(player);
    const gamepad = getGamepadMap(player);

    for (let nesBtn = 0; nesBtn < NES_BUTTON_NAMES.length; nesBtn++) {
      const name = NES_BUTTON_NAMES[nesBtn]!;

      const kbdCodes: string[] = [];
      for (const code of Object.keys(keyboard)) {
        if (keyboard[code] === nesBtn) kbdCodes.push(code);
      }
      const padIdxs: number[] = [];
      for (const idxStr of Object.keys(gamepad)) {
        if (gamepad[Number(idxStr)] === nesBtn) padIdxs.push(Number(idxStr));
      }
      padIdxs.sort((a, b) => a - b);

      const row = document.createElement("tr");

      const nameCell = document.createElement("td");
      nameCell.className = "btn-name";
      nameCell.textContent = name;
      row.append(nameCell);

      const kbdCell = document.createElement("td");
      kbdCell.append(this.renderChips(
        kbdCodes,
        (c) => formatKeyCode(c),
        (c) => removeKeyboardBinding(player, c),
      ));
      const addKbd = document.createElement("button");
      addKbd.type = "button";
      addKbd.className = "add-binding";
      addKbd.textContent = "+ Key";
      addKbd.addEventListener("click", () => this.captureKey(addKbd, player, nesBtn));
      kbdCell.append(addKbd);
      row.append(kbdCell);

      const padCell = document.createElement("td");
      padCell.append(this.renderChips(
        padIdxs.map(String),
        (s) => formatGamepadButton(Number(s)),
        (s) => removeGamepadBinding(player, Number(s)),
      ));
      const addPad = document.createElement("button");
      addPad.type = "button";
      addPad.className = "add-binding";
      addPad.textContent = "+ Pad";
      addPad.addEventListener("click", () => this.captureGamepad(addPad, player, nesBtn));
      padCell.append(addPad);
      row.append(padCell);

      tbody.append(row);
    }

    table.append(tbody);
    return table;
  }

  private renderFooter(player: number): HTMLElement {
    const footer = document.createElement("div");
    footer.className = "modal-footer";

    const reset = document.createElement("button");
    reset.type = "button";
    reset.className = "secondary-btn";
    reset.textContent = "Reset this player";
    reset.addEventListener("click", () => resetPlayerBindings(player));
    footer.append(reset);

    const done = document.createElement("button");
    done.type = "button";
    done.className = "primary-btn";
    done.textContent = "Done";
    done.addEventListener("click", () => this.dialog.close());
    footer.append(done);

    return footer;
  }

  private renderChips<T extends string>(
    values: T[],
    label: (v: T) => string,
    remove: (v: T) => void,
  ): HTMLElement {
    const wrap = document.createElement("span");
    wrap.className = "binding-chips";
    if (values.length === 0) {
      const empty = document.createElement("span");
      empty.className = "binding-empty";
      empty.textContent = "—";
      wrap.append(empty);
      return wrap;
    }
    for (const v of values) {
      const chip = document.createElement("span");
      chip.className = "binding-chip";
      const text = document.createElement("span");
      text.textContent = label(v);
      chip.append(text);
      const rm = document.createElement("button");
      rm.type = "button";
      rm.className = "chip-remove";
      rm.setAttribute("aria-label", `Remove ${label(v)}`);
      rm.textContent = "×";
      rm.addEventListener("click", () => remove(v));
      chip.append(rm);
      wrap.append(chip);
    }
    return wrap;
  }

  private captureKey(triggerBtn: HTMLButtonElement, player: number, nesBtn: number): void {
    this.cancelActiveCapture();
    const originalText = triggerBtn.textContent;
    triggerBtn.textContent = "Press a key…";
    triggerBtn.classList.add("capturing");

    const onKey = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      cleanup();
      if (e.key === "Escape") {
        // Treat Escape as cancel — and prevent the native dialog dismiss
        // that would otherwise fire on the same keystroke.
        return;
      }
      addKeyboardBinding(player, nesBtn, e.code);
      // render() will be invoked via the bindings onChange listener.
    };
    const onClick = (e: MouseEvent): void => {
      // A click on another rebind button cancels this capture. Don't
      // swallow the click — let it propagate so the new button can start
      // its own capture.
      if (e.target !== triggerBtn) cleanup();
    };
    const cleanup = (): void => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("click", onClick, true);
      triggerBtn.textContent = originalText;
      triggerBtn.classList.remove("capturing");
      this.cancelCapture = null;
    };

    window.addEventListener("keydown", onKey, true);
    window.addEventListener("click", onClick, true);
    this.cancelCapture = cleanup;
  }

  private captureGamepad(triggerBtn: HTMLButtonElement, player: number, nesBtn: number): void {
    this.cancelActiveCapture();
    const originalText = triggerBtn.textContent;
    triggerBtn.textContent = "Press a button…";
    triggerBtn.classList.add("capturing");

    // Capture on the pad assigned to this player so a button press lands
    // on the right profile. Falls back to the first connected pad if the
    // player has no assignment yet (so "+ Pad" still works pre-assign).
    const padIndex = getPadIndex(player);
    const pickPad = (): Gamepad | null =>
      padAtIndex(padIndex) ?? (padIndex === null ? connectedPads()[0] ?? null : null);

    // Snapshot what's currently held so we only capture newly-pressed
    // buttons. Otherwise a stuck D-pad would fire.
    let previous: PadState = snapshotPad(pickPad());
    let rafHandle: number | null = null;

    const tick = (): void => {
      const pad = pickPad();
      const now = snapshotPad(pad);
      for (const idx of now) {
        if (!previous.has(idx)) {
          addGamepadBinding(player, nesBtn, idx);
          cleanup();
          return;
        }
      }
      previous = now;
      rafHandle = requestAnimationFrame(tick);
    };
    const onClick = (e: MouseEvent): void => {
      if (e.target !== triggerBtn) cleanup();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cleanup();
      }
    };
    const cleanup = (): void => {
      if (rafHandle !== null) cancelAnimationFrame(rafHandle);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKey, true);
      triggerBtn.textContent = originalText;
      triggerBtn.classList.remove("capturing");
      this.cancelCapture = null;
    };

    rafHandle = requestAnimationFrame(tick);
    window.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKey, true);
    this.cancelCapture = cleanup;
  }
}
